import { checkApiAuthorization } from '@zg-rentals/particles-js-auth';
import { createRequestHeaders, requester } from '@zg-rentals/particles-js-requester';
import type { AxiosInstance, AxiosResponse, Method } from 'axios';

import { PROXY_PATH } from '../constants/endpoints';
import { setCookieResponse } from '../actions/requestActions';
import { getScreeningBrownoutMessage, isBrownoutOn } from './brownoutUtil';
import { listingApplicationsTabLoadingStatusUpdate } from '../actions/loadingStatusActionCreators';
import { updateScreeningBrownoutMessage } from '../actions/screeningBrownoutActionCreators';
import { addApiError } from './addApiError';
import queryUtil from './queryUtils';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import { logError } from '@zg-rentals/log-error';
import { getGlobalMonitor } from '@zg-rentals/monitor-base';

const monitor = getGlobalMonitor();

export default function localRequester<T>({
  method = 'get',
  path = '',
  params = {},
  body = {},
  skipAuthValidation = false,
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
} = {}): Promise<AxiosResponse<T, any>> {
  // @ts-ignore
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (dispatch: any, getState: any) => {
    const state = getState();

    const logger = getGlobalLogger(`${__filename} ${method} ${path}`)!;

    path = getUrlWithParamsAppended(path, params);

    const headers = createRequestHeaders({
      userAgent: state.app.userAgent,
      cookies: state.user.originalServerSideRequestCookies,
      ip: state.app.ip,
      brand: 'zillow',
      logger,
    });

    let api = state.app.zgApi;
    if (__BROWSER__) {
      headers['x-csrf-token'] = state.app.csrfToken;
      api = window.location.origin;
    }

    const client = requester({
      api,
      proxyPath: PROXY_PATH,
      headers,
    });

    logger.debug({ path }, 'Process request');

    monitor?.count({ name: 'apiCall' });
    // sanitizing the path for the metric
    const sanitizedPath = queryUtil.removeQueryParameters(path);
    monitor?.count({ name: `apiCall ${sanitizedPath}` });

    return request<T>(client, path, method as Method, body)
      .catch((error = {}) => {
        addApiError(error, path);

        const { config, response = {} } = error;
        !skipAuthValidation && checkApiAuthorization({ res: response, brand: 'zillow' });
        logger.debug({ config }, 'axios %d %s', response.status, response.statusText);
        if (error.response) {
          monitor?.count({ name: `apiAxiosError ${sanitizedPath} ${response.status}` });

          if (response.status >= 500) {
            monitor?.count({ name: 'apiErrorCode 5xx' });
          } else if (response.status >= 400) {
            monitor?.count({ name: 'apiErrorCode 4xx' });
          }
        } else if (error.request) {
          // No response received from server. Likely a timeout or client internet issue.
          monitor?.count({ name: `apiAxiosError ${sanitizedPath} connectionIssue` });
        } else {
          monitor?.count({ name: `apiAxiosError ${sanitizedPath} unknownIssue` });
        }
        return Promise.reject(error);
      })
      .then((response = {} as AxiosResponse) => {
        const { data = {} as any, status, statusText } = response;
        logger.debug({
          msg: `parsing requester response`,
          status,
          statusText,
          data,
        });

        if (data.error) {
          addApiError(data.error, path);
          const error = new Error(data.error);
          if (isBrownoutOn(response)) {
            try {
              window.stop();
            } catch (exception) {
              document.execCommand('Stop');
            }
            dispatch(
              listingApplicationsTabLoadingStatusUpdate({
                message: data.error.message,
                isError: true,
                showSpinner: false,
                show: true,
                showCustomerSupportMessage: true,
              }),
            );

            logger.debug('Rental Applications is browned out for maintenance');
            return Promise.reject(response);
          }
          const screeningBrownoutMessage = getScreeningBrownoutMessage(response);
          if (screeningBrownoutMessage) {
            dispatch(updateScreeningBrownoutMessage(screeningBrownoutMessage));
          }
          logError({ error });
          monitor?.count({ name: `apiError ${sanitizedPath} ${status}` });

          return Promise.reject(error);
        }

        // update server side request cookies based on api response
        // used by renderer.js to return the correct cookeis in header of o.g. request
        if (response.headers['set-cookie']) {
          dispatch(setCookieResponse(response.headers['set-cookie']));
        }

        return response;
      });
  };
}

function request<T>(client: AxiosInstance, path: string, method: Method, data?: object) {
  if (method.toLowerCase() === 'get') {
    return client.get<T>(path);
  }
  return client.post<T>(path, data);
}

function getUrlWithParamsAppended(url: string, params: { [key: string]: string }) {
  const nonNullParams = Object.entries(params).filter(([_, value]) => value != null);
  if (nonNullParams.length > 0) {
    const urlParts = url.split('?');
    const firstUrlPart = urlParts[0];
    const searchParams = urlParts[1] || '';
    const urlSearchParams = new URLSearchParams(`?${searchParams}`);

    nonNullParams.forEach(([key, value]) => {
      if (!urlSearchParams.has(key)) {
        urlSearchParams.append(key, value);
      }
    });

    return `${firstUrlPart}?${urlSearchParams.toString()}`;
  }
  return url;
}
